import { fromZonedTime } from 'date-fns-tz';
import { ParsedUrlQuery } from 'querystring';
import type { BlogPosting, WithContext } from 'schema-dts';

import {
	LOCALE,
	PAYWALLED_CONTENT_CLASSNAME,
	PRODUCTION_WEBSITE_URL,
	TIMEZONE_NAME,
	ZINE_POSTS_PER_PAGE,
	ZINE_URL,
} from '@data/constants';
import { DatoZinePostPreview, DatoZinePostRecord } from '@interfaces';

export const getPageNumberFromQuery = (query: ParsedUrlQuery) => {
	return typeof query.page === 'string' ? Number(query.page) : 1;
};

export const getSkipValueFromCurrentPage = (pageNumber: number) => {
	return pageNumber === 1
		? 0
		: pageNumber * ZINE_POSTS_PER_PAGE - ZINE_POSTS_PER_PAGE;
};

export const getTotalPages = (totalPosts: number) => {
	return Math.ceil(totalPosts / ZINE_POSTS_PER_PAGE);
};

export const checkPaywalled = ({
	firstPublishedAt,
	paywalledAt,
}: DatoZinePostRecord | DatoZinePostPreview) => {
	let paywalledDate: Date | undefined;

	if (paywalledAt) {
		// Midnight for defined timezone in UTC
		paywalledDate = fromZonedTime(`${paywalledAt} 00:00:00`, TIMEZONE_NAME);
	} else if (firstPublishedAt) {
		// Fall back to the published date plus one month
		paywalledDate = new Date(firstPublishedAt);
		paywalledDate.setMonth(paywalledDate.getMonth() + 1);
	}

	if (paywalledDate) {
		return Date.now() > paywalledDate.getTime();
	}

	return false;
};

export const getZinePostRecordSchema = (record: DatoZinePostRecord) => {
	const {
		authors,
		slug,
		updatedAt,
		firstPublishedAt,
		title,
		seoMetaTags,
		featuredImage,
		thumbnailImage,
	} = record;

	const imageFile = thumbnailImage || featuredImage;

	const descriptionMetaTag = seoMetaTags.find(({ attributes }) => {
		return attributes?.name === 'description';
	});

	const data: WithContext<BlogPosting> = {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		author: authors.map(({ name }) => {
			return {
				'@type': 'Person',
				name,
			};
		}),
		dateModified: updatedAt,
		description: descriptionMetaTag?.attributes?.content,
		hasPart: {
			'@type': 'WebPageElement',
			isAccessibleForFree: 'False',
			cssSelector: `.${PAYWALLED_CONTENT_CLASSNAME}`,
		},
		headline: title,
		inLanguage: 'en',
		image: imageFile
			? [
					{
						'@type': 'ImageObject',
						url: imageFile.url,
					},
				]
			: undefined,
		isAccessibleForFree: false,
		publisher: {
			'@type': 'Organization',
			name: 'Elsewhere',
		},
		url: `${PRODUCTION_WEBSITE_URL}${ZINE_URL}/${slug}`,
	};

	if (typeof firstPublishedAt === 'string') {
		const firstPublishedDate = new Date(firstPublishedAt);

		const copyrightYear = firstPublishedDate.toLocaleString([LOCALE], {
			timeZone: TIMEZONE_NAME,
			year: 'numeric',
		});

		Object.assign(data, {
			copyrightYear: Number(copyrightYear),
			datePublished: firstPublishedAt,
		});
	}

	return data;
};
